import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { mockData } from "../mock";

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { hero, memorySection, celebration, gallery } = mockData.home;

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Image */}
          <div
            className="relative group"
            style={{
              animation: "fadeInUp 0.8s ease-out",
            }}
          >
            <div className="absolute -inset-4 bg-[#E8D5B5] opacity-50 rounded-lg transform rotate-2 transition-transform duration-300 group-hover:rotate-3"></div>
            <div className="relative bg-white p-6 shadow-xl rounded-lg overflow-hidden">
              <img
                src={hero.mainImage}
                alt="Valerie Bastet"
                className="w-full h-auto object-cover rounded"
                loading="eager"
              />
            </div>
            {/* Decorative leaf */}
            <div className="absolute -bottom-8 -right-8 opacity-60">
              <svg
                width="120"
                height="120"
                viewBox="0 0 120 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M60 60C50 70 40 80 35 90C30 100 35 110 45 110C55 110 70 100 80 90"
                  stroke="#C9B89A"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M60 60C70 50 80 40 90 35C100 30 110 35 110 45C110 55 100 70 90 80"
                  stroke="#C9B89A"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </div>
          </div>

          {/* Right: Text */}
          <div
            className="space-y-6"
            style={{
              animation: "fadeInUp 0.8s ease-out 0.2s both",
            }}
          >
            <p
              className="text-sm tracking-widest uppercase text-[#8B7355]"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {hero.subtitle}
            </p>
            <h1
              className="text-6xl md:text-7xl font-light tracking-wide text-[#3A3A3A] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {hero.title}
            </h1>
            <p
              className="text-lg leading-relaxed text-[#5A5A5A] max-w-xl"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {hero.mainText}
            </p>
          </div>
        </div>
      </section>

      {/* In Loving Memory Section */}
      <section className="bg-[#DDE5DC] py-20 my-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Title and Years */}
          <div className="text-center mb-12">
            <h2
              className="text-5xl md:text-6xl font-light mb-4 text-[#3A3A3A] tracking-wide"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {memorySection.title}
            </h2>
            <p
              className="text-2xl text-[#7A7A7A] tracking-widest"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {memorySection.years}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="relative group">
                <div className="absolute -inset-4 bg-[#C9D5C8] opacity-40 rounded-lg transform -rotate-2 transition-transform duration-300 group-hover:-rotate-3"></div>
                <div className="relative bg-white p-6 shadow-xl rounded-lg overflow-hidden">
                  <img
                    src={memorySection.image}
                    alt="Valerie with her dog"
                    className="w-full h-auto object-cover rounded"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2 space-y-6">
              {memorySection.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-base leading-relaxed text-[#5A5A5A]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Golden Leaf Divider */}
      <div className="flex justify-center my-16">
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M40 40C35 30 30 20 28 15C26 10 30 5 35 8C40 11 45 21 50 31"
            stroke="#D4AF37"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M40 40C45 30 50 20 52 15C54 10 50 5 45 8C40 11 35 21 30 31"
            stroke="#D4AF37"
            strokeWidth="2"
            fill="none"
          />
          <circle cx="40" cy="40" r="3" fill="#D4AF37" />
        </svg>
      </div>

      {/* Celebration Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2
            className="text-5xl md:text-6xl font-light mb-8 text-[#3A3A3A] tracking-wide"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {celebration.title}
          </h2>
        </div>

        <div className="max-w-4xl mx-auto space-y-6 mb-12">
          {celebration.text.split("\n\n").map((paragraph, index) => (
            <p
              key={index}
              className="text-base leading-relaxed text-[#5A5A5A] text-center"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {paragraph}
            </p>
          ))}
          <p
            className="text-base font-semibold text-center text-[#3A3A3A] italic mt-8"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {celebration.dressCode}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-4 bg-[#E8D5B5] opacity-50 rounded-lg transform rotate-1 transition-transform duration-300 group-hover:rotate-2"></div>
            <div className="relative bg-white p-6 shadow-xl rounded-lg overflow-hidden">
              <img
                src={celebration.image}
                alt="Celebration"
                className="w-full h-auto object-cover rounded"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Golden Leaf Divider */}
      <div className="flex justify-center my-16">
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M40 40C35 30 30 20 28 15C26 10 30 5 35 8C40 11 45 21 50 31"
            stroke="#D4AF37"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M40 40C45 30 50 20 52 15C54 10 50 5 45 8C40 11 35 21 30 31"
            stroke="#D4AF37"
            strokeWidth="2"
            fill="none"
          />
          <circle cx="40" cy="40" r="3" fill="#D4AF37" />
        </svg>
      </div>

      {/* Photo Gallery Preview */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {gallery.map((image, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-lg shadow-lg group cursor-pointer"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
              }}
            >
              <img
                src={image}
                alt={`Gallery ${index + 1}`}
                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Leaf decorations */}
        <div className="flex justify-center items-center space-x-8 mb-8">
          <svg
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 30C15 25 10 20 8 15"
              stroke="#C9B89A"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d="M30 20C25 15 20 10 18 5"
              stroke="#C9B89A"
              strokeWidth="1.5"
              fill="none"
            />
          </svg>
          <svg
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M40 30C45 25 50 20 52 15"
              stroke="#C9B89A"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d="M30 20C35 15 40 10 42 5"
              stroke="#C9B89A"
              strokeWidth="1.5"
              fill="none"
            />
          </svg>
        </div>

        {/* Show More Button */}
        <div className="text-center">
          <Link
            to="/gallery"
            className="inline-block px-8 py-3 border-2 border-[#8B7355] text-[#8B7355] tracking-widest uppercase text-sm hover:bg-[#8B7355] hover:text-white transition-all duration-300"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Show more
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;